import React from "react";
import { ChevronDown } from "lucide-react";

// CollapsibleSection — a thin disclosure wrapper used by all Analytics V3 phases.
// Defaults to open on desktop and the explicit `defaultOpen` flag on mobile.
// Keeping the chevron AMG-red so users get an obvious "I can collapse this" cue
// without breaking the dense Q Drives palette.
export function CollapsibleSection({ title, subtitle, headerRight, defaultOpen = true, testid, children }) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <section className="mb-6" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        data-testid={testid ? `${testid}-toggle` : undefined}
        className="w-full flex items-center justify-between gap-3 text-left mb-3 group"
      >
        <div className="min-w-0">
          <div className="qd-overline flex items-center gap-2">
            <ChevronDown
              size={14}
              className={`text-[#FF1E1E] transition-transform ${open ? "rotate-0" : "-rotate-90"}`}
            />
            {title}
          </div>
          {subtitle && <div className="text-[11px] text-white/45 mt-1 pl-5">{subtitle}</div>}
        </div>
        {headerRight && (
          <div onClick={(e) => e.stopPropagation()} className="shrink-0">
            {headerRight}
          </div>
        )}
      </button>
      {open && <div data-testid={testid ? `${testid}-body` : undefined}>{children}</div>}
    </section>
  );
}

export function PageHeader({ title, subtitle, right }) {
  return (
    <div className="flex items-center justify-between mb-6 pb-4 border-b border-[#2A2A2A]">
      <div>
        <div className="qd-overline mb-1">{subtitle}</div>
        <h1 className="qd-h1 text-2xl text-white">{title}</h1>
      </div>
      <div className="flex items-center gap-3">
        {right}
        <div className="flex items-center gap-2 font-mono-data text-[10px] tracking-widest uppercase text-white/40">
          <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> LIVE
        </div>
      </div>
    </div>
  );
}

export function Card({ children, className = "" }) {
  return <div className={`qd-card ${className}`}>{children}</div>;
}

export function TempBadge({ value, onChange, testid }) {
  const opts = [
    { v: "hot", l: "HOT", color: "#FF1E1E" },
    { v: "warm", l: "WARM", color: "#F5A623" },
    { v: "cold", l: "COLD", color: "#6FA8DC" },
  ];
  if (onChange) {
    return (
      <select
        data-testid={testid}
        value={value || "warm"}
        onChange={(e) => onChange(e.target.value)}
        className="bg-[#0F0F0F] border border-[#2A2A2A] text-[10px] font-mono-data tracking-widest uppercase px-2 py-1 outline-none"
        style={{ color: opts.find(o => o.v === (value || "warm"))?.color }}
      >
        {opts.map(o => <option key={o.v} value={o.v} style={{ color: "#fff", background: "#0F0F0F" }}>{o.l}</option>)}
      </select>
    );
  }
  const cur = opts.find(o => o.v === (value || "warm"));
  return <span className="text-[10px] font-mono-data tracking-widest uppercase font-bold" style={{ color: cur.color }}>{cur.l}</span>;
}

export function StatusPill({ status }) {
  const colors = {
    submitted: "#C0C0C0",
    under_review: "#F5A623",
    inspection_scheduled: "#6FA8DC",
    buyer_matching_active: "#8E44AD",
    live_offers_active: "#FF1E1E",
    offer_finalized: "#22C55E",
  };
  const c = colors[status] || "#808080";
  return (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-mono-data tracking-widest uppercase font-bold" style={{ color: c }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
      {status}
    </span>
  );
}

export function Drawer({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="admin-drawer">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative w-full max-w-xl h-full bg-[#0A0A0A] border-l border-[#2A2A2A] flex flex-col">
        <div className="px-5 py-4 border-b border-[#2A2A2A] flex items-center justify-between">
          <div className="qd-overline">{title}</div>
          <button data-testid="drawer-close" onClick={onClose} className="text-white/60 hover:text-white text-sm">CLOSE</button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

export function Toolbar({ children }) {
  return <div className="flex items-center gap-3 mb-4 flex-wrap">{children}</div>;
}

export function ToolbarInput({ value, onChange, placeholder, testid }) {
  return (
    <input
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="bg-[#0F0F0F] border border-[#2A2A2A] text-white text-xs font-mono-data px-3 py-2 outline-none focus:border-[#FF1E1E] min-w-[200px]"
    />
  );
}

export function ToolbarSelect({ value, onChange, options, testid }) {
  return (
    <select
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-[#0F0F0F] border border-[#2A2A2A] text-white text-xs font-mono-data px-3 py-2 outline-none focus:border-[#FF1E1E]"
    >
      {options.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

export function StatTile({ label, value, accent }) {
  return (
    <div className="bg-[#0E0E0E] p-4 border border-[#2A2A2A]">
      <div className="text-[10px] font-mono-data tracking-widest text-white/40 uppercase mb-2">{label}</div>
      <div className={`font-display text-2xl font-extrabold ${accent ? "text-[#FF1E1E]" : "text-white"}`}>{value}</div>
    </div>
  );
}

export function fmtINR(n) {
  if (!n) return "—";
  return `₹${(n / 100000).toFixed(2)}L`;
}

export function timeAgo(iso) {
  if (!iso) return "—";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}


// ---------- Acquisition Pipeline ----------
export const ACQUISITION_STAGES = [
  "new_lead", "contacted", "offer_sent", "negotiating",
  "inspection_scheduled", "inspection_completed", "final_offer_sent",
  "won", "lost", "follow_up",
];

const STAGE_META = {
  new_lead: { l: "New Lead", c: "#6FA8DC" },
  contacted: { l: "Contacted", c: "#F5A623" },
  offer_sent: { l: "Offer Sent", c: "#8E44AD" },
  negotiating: { l: "Negotiating", c: "#FF1E1E" },
  inspection_scheduled: { l: "Inspection Scheduled", c: "#22C55E" },
  inspection_completed: { l: "Inspection Done", c: "#22C55E" },
  final_offer_sent: { l: "Final Offer", c: "#8E44AD" },
  won: { l: "Won", c: "#22C55E" },
  lost: { l: "Lost", c: "#808080" },
  follow_up: { l: "Follow Up", c: "#F5A623" },
};

// Seller-friendly labels — mirrors backend STAGE_SELLER_LABEL exactly.
// Used on the seller's MyActivity + LiveOffers / pipeline tracker.
export const STAGE_SELLER_LABEL = {
  new_lead: "Submitted",
  contacted: "Under Review",
  offer_sent: "Offer Sent",
  negotiating: "Negotiation In Progress",
  inspection_scheduled: "Inspection Scheduled",
  inspection_completed: "Inspection Completed",
  final_offer_sent: "Final Offer Ready",
  won: "Deal Confirmed",
  lost: "Closed",
  follow_up: "Follow Up Later",
};

export function StageBadge({ stage }) {
  const m = STAGE_META[stage] || { l: stage || "—", c: "#808080" };
  return (
    <span
      className="inline-flex items-center gap-1.5 text-[10px] tracking-wide px-2 py-[3px] border"
      style={{ color: m.c, borderColor: `${m.c}55`, background: `${m.c}0F` }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: m.c }} />
      {m.l}
    </span>
  );
}

export function StageSelect({ value, onChange, testid }) {
  return (
    <select
      data-testid={testid}
      value={value || "new_lead"}
      onChange={(e) => onChange(e.target.value)}
      className="bg-[#0F0F0F] border border-[#2A2A2A] text-white text-xs px-3 py-2 outline-none focus:border-[#FF1E1E]"
    >
      {ACQUISITION_STAGES.map((s) => (
        <option key={s} value={s} style={{ background: "#0F0F0F" }}>{STAGE_META[s]?.l || s}</option>
      ))}
    </select>
  );
}

/** AgeBadge — warning >24h, stale >72h, critical = stale + high urgency.
 *  `last` is ISO datetime of last contact (or createdAt fallback).
 *  `urgency` should be 'hot' for critical flagging. */
export function AgeBadge({ last, urgency }) {
  if (!last) return null;
  const hours = (Date.now() - new Date(last).getTime()) / 3600000;
  let label = null, color = null;
  if (hours > 72 && urgency === "hot") { label = "Critical"; color = "#FF1E1E"; }
  else if (hours > 72) { label = "Stale"; color = "#808080"; }
  else if (hours > 24) { label = "Aging"; color = "#F5A623"; }
  if (!label) return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] tracking-wide px-1.5 py-[2px] border"
      style={{ color, borderColor: `${color}66`, background: `${color}0F` }}
    >
      {label} · {Math.floor(hours)}h
    </span>
  );
}

/** Returns row-level background tint based on age + urgency. */
export function ageRowBg(last, urgency) {
  if (!last) return "transparent";
  const hours = (Date.now() - new Date(last).getTime()) / 3600000;
  if (hours > 72 && urgency === "hot") return "rgba(255,30,30,0.05)";
  if (hours > 72) return "rgba(128,128,128,0.05)";
  if (hours > 24) return "rgba(245,166,35,0.04)";
  return "transparent";
}
